<template>
  <!-- SMS Detail Modal - Apple Glass Design -->
  <transition name="modal">
    <div
      v-if="isOpen"
      class="fixed inset-0 z-50 overflow-y-auto"
      @click.self="closeModal"
    >
      <!-- Backdrop -->
      <div class="fixed inset-0 bg-black/60 backdrop-blur-sm" @click="closeModal"></div>
      
      <!-- Modal Container -->
      <div class="flex min-h-screen items-center justify-center p-4">
        <div
          class="relative w-full max-w-lg bg-white rounded-2xl border border-gray-200 shadow-2xl"
          @click.stop
        >
          <!-- Header -->
          <div class="flex items-center justify-between p-4 border-b border-gray-200">
            <h3 class="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <svg class="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z"/>
              </svg>
              SMS Delivery Details
            </h3>
            <button
              @click="closeModal"
              class="text-gray-500 hover:text-gray-900 transition-colors p-2 hover:bg-gray-100 rounded-lg"
            >
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
              </svg>
            </button>
          </div>

          <!-- Content -->
          <div class="p-2 space-y-2">
            
            <!-- SMS Preview -->
            <div class="bg-gray-50 rounded-xl p-3 sm:p-4">
              <h4 class="text-sm font-semibold text-gray-900 mb-3">SMS Preview</h4>
              <!-- Phone Frame -->
              <div class="bg-black rounded-[2.5rem] p-1.5 shadow-2xl w-full">
                    <!-- Screen -->
                    <div class="bg-zinc-900 rounded-[2rem] overflow-hidden w-full">
                      <!-- Status Bar -->
                      <div class="bg-black px-3 sm:px-6 py-2 flex justify-between items-center text-white text-sm overflow-hidden">
                        <div class="flex items-center space-x-1 min-w-0 flex-1">
                          <div class="flex space-x-1 flex-shrink-0">
                            <div class="w-1 h-1 bg-white rounded-full"></div>
                            <div class="w-1 h-1 bg-white rounded-full"></div>
                            <div class="w-1 h-1 bg-white/50 rounded-full"></div>
                          </div>
                          <span class="ml-2 text-xs truncate">engagr</span>
                        </div>
                        <div class="text-xs flex-shrink-0 mx-2">{{ getCurrentTime() }}</div>
                        <div class="flex items-center space-x-1 flex-shrink-0">
                          <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                            <path fill-rule="evenodd" d="M17.778 8.222c-4.296-4.296-11.26-4.296-15.556 0A1 1 0 01.808 6.808c5.076-5.077 13.308-5.077 18.384 0a1 1 0 01-1.414 1.414zM14.95 11.05a7 7 0 00-9.9 0 1 1 0 01-1.414-1.414 9 9 0 0112.728 0 1 1 0 01-1.414 1.414zM12.12 13.88a3 3 0 00-4.24 0 1 1 0 01-1.415-1.414 5 5 0 017.07 0 1 1 0 01-1.415 1.414zM9 16a1 1 0 011-1h.01a1 1 0 110 2H10a1 1 0 01-1-1z" clip-rule="evenodd"></path>
                          </svg>
                          <div class="text-xs">100%</div>
                        </div>
                      </div>
                      
                      <!-- Contact Header -->
                      <div class="bg-zinc-900 px-3 sm:px-4 py-2.5 flex items-center justify-between border-b border-zinc-800 overflow-hidden">
                        <div class="min-w-0 flex-1 pr-2">
                          <div class="text-white font-medium text-sm truncate">Garry Warren Smith Used Cars Chadstone</div>
                          <div class="text-gray-400 text-xs">Active now</div>
                        </div>
                        <div class="flex-shrink-0">
                          <svg class="w-6 h-6 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"/>
                          </svg>
                        </div>
                      </div>
                      
                      <!-- Messages Container -->
                      <div class="bg-zinc-950 min-h-[300px] p-2.5 sm:p-3 space-y-3 overflow-x-hidden">
                        <!-- SMS Message Bubble -->
                        <div class="flex justify-start">
                          <div class="w-full max-w-[90%] sm:max-w-sm lg:max-w-lg">
                            <div class="bg-gradient-to-br from-green-600 to-green-700 text-white p-2.5 rounded-2xl rounded-bl-md shadow-lg break-words sms-message-bubble">
                              <div class="text-sm leading-relaxed whitespace-pre-wrap break-words sms-message-text">
                                {{ getSMSMessage() }}
                              </div>
                            </div>
                            <div class="text-xs text-gray-500 mt-1 px-2 flex items-center justify-between break-words">
                              <span class="break-all">{{ getCurrentTime() }} • {{ formatPhoneNumber(getSMSFromNumber()) }}</span>
                            </div>
                          </div>
                        </div>
                        
                        <!-- Delivery Status -->
                        <div class="text-center">
                          <div 
                            class="inline-flex items-center text-xs px-3 py-1 rounded-full"
                            :class="getDeliveryStatusClass()"
                          >
                            <div 
                              class="w-2 h-2 rounded-full mr-2"
                              :class="sms.delivered ? 'bg-green-400 animate-pulse' : 'bg-gray-400'"
                            ></div>
                            {{ getDeliveryStatusText() }}
                          </div>
                        </div>
                      </div>
                      
                      <!-- Message Input Area -->
                      <div class="bg-zinc-900 p-2.5 border-t border-zinc-800">
                        <div class="flex items-center space-x-2 w-full">
                          <div class="flex-1 bg-zinc-800 rounded-full px-4 py-2 text-gray-400 text-sm min-w-0 overflow-hidden">
                            <span class="truncate block">Message</span>
                          </div>
                          <div class="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0">
                            <svg class="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                              <path d="M10.894 2.553a1 1 0 00-1.788 0l-7 14a1 1 0 001.169 1.409l5-1.429A1 1 0 009 15.571V11a1 1 0 112 0v4.571a1 1 0 00.725.962l5 1.429a1 1 0 001.17-1.409l-7-14z"></path>
                            </svg>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
              
              <!-- From/To Info -->
              <div class="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div class="bg-gray-100 border border-gray-200 rounded-xl p-2.5 break-words">
                  <h5 class="text-xs font-medium text-gray-600 mb-1">From Number</h5>
                  <p class="text-sm text-gray-900 break-all">{{ formatPhoneNumber(getSMSFromNumber()) }}</p>
                </div>
                <div class="bg-gray-100 border border-gray-200 rounded-xl p-2.5 break-words">
                  <h5 class="text-xs font-medium text-gray-600 mb-1">To Number</h5>
                  <p class="text-sm text-gray-900 break-all">{{ formatPhoneNumber(sms.phoneNumber) }}</p>
                </div>
              </div>
            </div>

            <!-- Error Details (if failed) -->
            <div
              v-if="sms.failed && (sms.errorMessage || sms.errorCode)"
              class="bg-red-50 border border-red-300 rounded-xl p-4"
            >
              <div class="flex items-start gap-3">
                <svg class="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clip-rule="evenodd"/>
                </svg>
                <div>
                  <div class="text-sm font-semibold text-red-700 mb-1">Delivery Failed</div>
                  <div class="text-sm text-gray-900">{{ sms.errorMessage }}</div>
                  <div v-if="sms.errorCode" class="text-xs text-red-600 mt-1">
                    Error Code: {{ sms.errorCode }}
                  </div>
                </div>
              </div>
            </div>

            <!-- Delivery Timeline -->
            <div>
              <h4 class="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clip-rule="evenodd"/>
                </svg>
                Delivery Timeline
              </h4>
              
              <div v-if="sms.events && sms.events.length > 0" class="space-y-3">
                <div
                  v-for="(evt, index) in sortedEvents"
                  :key="index"
                  class="flex items-start gap-4 relative"
                >
                  <!-- Timeline Line -->
                  <div
                    v-if="index < sms.events.length - 1"
                    class="absolute left-3 top-8 w-0.5 h-full bg-gradient-to-b from-blue-500/30 to-transparent"
                  ></div>
                  
                  <!-- Event Icon -->
                  <div
                    class="relative z-10 flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center"
                    :class="getEventIconClass(evt.type)"
                  >
                    <div class="w-2 h-2 rounded-full bg-current"></div>
                  </div>
                  
                  <!-- Event Details -->
                  <div class="flex-1 bg-gray-50 rounded-lg p-3 border border-gray-200">
                    <div class="flex items-center justify-between">
                      <span class="text-sm font-medium text-gray-900 capitalize">{{ evt.type }}</span>
                      <span class="text-xs text-gray-600">{{ formatEventTime(evt.date) }}</span>
                    </div>
                    <div class="text-xs text-gray-500 mt-1">
                      {{ formatRelativeTime(evt.timestamp) }}
                    </div>
                  </div>
                </div>
              </div>
              
              <div v-else class="text-center py-8 text-gray-500">
                <svg class="w-12 h-12 mx-auto mb-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
                </svg>
                <p>No timeline events available</p>
              </div>
            </div>
            
          </div>

          <!-- Footer -->
          <div class="flex items-center justify-end gap-3 p-6 border-t border-gray-200">
            <button
              @click="closeModal"
              class="px-4 py-2 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-900 transition-colors border border-gray-300"
            >
              Close
            </button>
          </div>
          
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
export default {
  name: 'SMSDetailModal',
  components: {
  },
  props: {
    isOpen: {
      type: Boolean,
      default: false
    },
    sms: {
      type: Object,
      required: true,
      default: () => ({
        phoneNumber: '',
        status: 'unknown',
        events: [],
        cost: 0,
        numSegments: 0,
        errorCode: null,
        errorMessage: null,
        source: []
      })
    }
  },
  computed: {
    sortedEvents() {
      if (!this.sms.events) return [];
      return [...this.sms.events].sort((a, b) => a.timestamp - b.timestamp);
    }
  },
  methods: {
    closeModal() {
      this.$emit('close');
    },
    formatCost(cost) {
      if (!cost || cost === 0) return '$0.000';
      const amount = Math.abs(parseFloat(cost));
      return `$${amount.toFixed(3)}`;
    },
    formatEventTime(dateString) {
      if (!dateString) return 'N/A';
      try {
        const date = new Date(dateString);
        return date.toLocaleString('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit'
        });
      } catch (error) {
        return dateString;
      }
    },
    formatRelativeTime(timestamp) {
      if (!timestamp) return '';
      try {
        const date = new Date(timestamp * 1000);
        const now = new Date();
        const diffMs = now - date;
        const diffSecs = Math.floor(diffMs / 1000);
        const diffMins = Math.floor(diffSecs / 60);
        const diffHours = Math.floor(diffMins / 60);
        const diffDays = Math.floor(diffHours / 24);

        if (diffDays > 0) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
        if (diffHours > 0) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
        if (diffMins > 0) return `${diffMins} min${diffMins > 1 ? 's' : ''} ago`;
        return `${diffSecs} sec${diffSecs > 1 ? 's' : ''} ago`;
      } catch (error) {
        return '';
      }
    },
    getEventIconClass(eventType) {
      const type = eventType.toLowerCase();
      if (type === 'delivered') return 'text-green-600 bg-green-100';
      if (type === 'sent') return 'text-blue-600 bg-blue-100';
      if (type === 'queued') return 'text-yellow-600 bg-yellow-100';
      if (type === 'failed' || type === 'undelivered') return 'text-red-600 bg-red-100';
      return 'text-gray-600 bg-gray-100';
    },
    getCurrentTime() {
      const now = new Date();
      return now.toLocaleTimeString('en-US', { 
        hour: 'numeric', 
        minute: '2-digit',
        hour12: true 
      });
    },
    getSMSMessage() {
      // Try to get the actual message body from events
      if (this.sms.events && this.sms.events.length > 0) {
        const messageBody = this.sms.events[0].body;
        if (messageBody) {
          return messageBody;
        }
      }
      // Fallback to generic message
      return `Hi Test, Congratulations on the purchase of your Test... We would love for you to leave us review for Garry Warren Smith Used Cars Chadstone at https://search.google.com/local/writereview?placeid=ChIJf0T4kctr1moRtvlRzr-EskQ`;
    },
    getSMSFromNumber() {
      // Try to get from number from events
      if (this.sms.events && this.sms.events.length > 0 && this.sms.events[0].from) {
        return this.sms.events[0].from;
      }
      // Fallback
      return '+61427985617';
    },
    formatPhoneNumber(phone) {
      if (!phone) return 'N/A';
      // Format as +61 4XX XXX XXX
      const cleaned = phone.replace(/\D/g, '');
      if (cleaned.startsWith('61') && cleaned.length === 11) {
        return `+${cleaned.slice(0, 2)} ${cleaned.slice(2, 3)} ${cleaned.slice(3, 5)} ${cleaned.slice(5, 8)} ${cleaned.slice(8)}`;
      }
      return phone;
    },
    getDeliveryStatusText() {
      if (this.sms.delivered) return 'Delivered';
      if (this.sms.failed) return 'Failed';
      if (this.sms.sent) return 'Sent';
      if (this.sms.queued) return 'Queued';
      return 'Unknown';
    },
    getDeliveryStatusClass() {
      if (this.sms.delivered) return 'text-green-700 bg-green-100';
      if (this.sms.failed) return 'text-red-700 bg-red-100';
      if (this.sms.sent) return 'text-blue-700 bg-blue-100';
      if (this.sms.queued) return 'text-yellow-700 bg-yellow-100';
      return 'text-gray-700 bg-gray-100';
    }
  }
};
</script>

<style scoped>
/* Modal transition animations */
.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.3s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

.modal-enter-active > div > div,
.modal-leave-active > div > div {
  transition: transform 0.3s ease;
}

.modal-enter-from > div > div,
.modal-leave-to > div > div {
  transform: scale(0.9);
}

/* SMS Message Bubble - Prevent text overflow on mobile */
.sms-message-bubble {
  overflow-wrap: anywhere;
  word-break: break-word;
  hyphens: auto;
}

.sms-message-text {
  overflow-wrap: anywhere;
  word-break: break-word;
  hyphens: auto;
}
</style>

